// ts-rest contract for the book value history of the Desk Dashboard (SEP-297).
//
// The chart shows the total value in GBP of the book of the signed-in IM over
// time. It is not a performance chart: the value includes deposits,
// withdrawals and income. Each point comes from one dated source:
//
//   - `valuation`: the holdings grid upload of that day (the figure of record);
//   - `estimate`: the last upload, moved by the daily closes of its securities,
//     for a day with no upload (day and week ranges only);
//   - `month_report`: the month-end value of the monthly KPI reporting, for a
//     month with no upload (month ranges only);
//   - no source (a gap): no data for the date. The chart draws no line there.
//
// The server builds the series, the drill-down table and the change drivers
// from the same rows, so the chart, the table and the export agree.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** The ranges of the chart. */
export const BOOK_HISTORY_RANGES = ['1M', '3M', 'YTD', '1Y', '3Y'] as const;
export const bookHistoryRangeSchema = z.enum(BOOK_HISTORY_RANGES);
export type BookHistoryRange = z.infer<typeof bookHistoryRangeSchema>;

/** The length of the period of one point. */
export const bookHistoryGranularitySchema = z.enum(['day', 'week', 'month']);
export type BookHistoryGranularity = z.infer<typeof bookHistoryGranularitySchema>;

/** Where the value of a point comes from. See the module comment. */
export const BOOK_HISTORY_SOURCES = ['valuation', 'estimate', 'month_report'] as const;
export const bookHistorySourceSchema = z.enum(BOOK_HISTORY_SOURCES);
export type BookHistorySource = z.infer<typeof bookHistorySourceSchema>;

/**
 * The filters of the chart. Each one is a comma-separated list, and an absent
 * filter keeps each client. Each filter reads a field of the client, so it
 * gives the same clients on each range.
 */
export const bookHistoryFilterQuerySchema = z.object({
    clientIds: z.string().optional(),
    householdIds: z.string().optional(),
    /** `Client.clientKind` values, for example `individual,trust`. */
    clientKinds: z.string().optional(),
    /** `Client.riskRating` values. */
    riskRatings: z.string().optional(),
    /** `Client.serviceType` values: `Discretionary`, `Advisory`, `ExecutionOnly`. */
    serviceTypes: z.string().optional(),
});
export type BookHistoryFilterQuery = z.infer<typeof bookHistoryFilterQuerySchema>;

export const bookHistoryPointSchema = z.object({
    /** The last day of the period of the point. */
    date: isoDay,
    /** The value of the book, or null for a gap. */
    valueGbp: z.number().nullable(),
    /** Null for a gap. */
    source: bookHistorySourceSchema.nullable(),
    /**
     * The day of the data: the upload day of a valuation, the day of the
     * closes of an estimate, or the last day of the month of a month report.
     */
    valuedOn: isoDay.nullable(),
    /** An estimate only: the upload that the estimate moves. */
    baseValuedOn: isoDay.nullable(),
    /**
     * An estimate only: the percentage of the value of the upload that has a
     * daily close. The rest stays at its upload value.
     */
    pricedPct: z.number().nullable(),
    clientCount: z.number().int().nonnegative().nullable(),
});
export type BookHistoryPoint = z.infer<typeof bookHistoryPointSchema>;

export const bookHistoryOptionSchema = z.object({ id: z.string(), name: z.string() });

export const bookHistoryResponseSchema = z.object({
    range: bookHistoryRangeSchema,
    granularity: bookHistoryGranularitySchema,
    /** The first and last day of the range. */
    from: isoDay,
    to: isoDay,
    currency: z.literal('GBP'),
    /** The day of the latest data in the range, or null when the range has none. */
    asOf: isoDay.nullable(),
    points: z.array(bookHistoryPointSchema),
    /** The first holdings upload that the history holds, or null. */
    firstValuationOn: isoDay.nullable(),
    /**
     * False when a filter selects clients one by one (client, household, kind
     * or risk). The month reports name a client by name only, so such a
     * filter cannot use them.
     */
    monthReportsApply: z.boolean(),
    filterOptions: z.object({
        clients: z.array(bookHistoryOptionSchema),
        households: z.array(bookHistoryOptionSchema),
        clientKinds: z.array(z.string()),
        riskRatings: z.array(z.string()),
        serviceTypes: z.array(z.string()),
    }),
});
export type BookHistoryResponse = z.infer<typeof bookHistoryResponseSchema>;

/**
 * What changed the value between the previous point and this point. Each
 * field is null when the two sources cannot give it. The non-null fields add
 * up to `changeGbp`.
 */
export const bookHistoryDriversSchema = z.object({
    changeGbp: z.number(),
    /** The price change of the securities held on both days. */
    marketMovementGbp: z.number().nullable(),
    /** Buys, sells and cash changes of the clients held on both days. */
    tradesAndCashGbp: z.number().nullable(),
    /** The value of the clients that joined the book. */
    clientsJoinedGbp: z.number().nullable(),
    /** The value of the clients that left the book, as a negative number. */
    clientsLeftGbp: z.number().nullable(),
    /** A month report only: the deposits less the withdrawals. */
    netMoniesGbp: z.number().nullable(),
    /** A month report only: the income. */
    incomeGbp: z.number().nullable(),
    /** The part of the change that the other fields do not explain. */
    otherGbp: z.number().nullable(),
});
export type BookHistoryDrivers = z.infer<typeof bookHistoryDriversSchema>;

export const bookHistoryMoverSchema = z.object({
    name: z.string(),
    isin: z.string().nullable(),
    changeGbp: z.number(),
});

export const bookHistoryClientRowSchema = z.object({
    /** Null for a month report row, which names the client by name only. */
    clientId: z.string().nullable(),
    name: z.string(),
    serviceType: z.string().nullable(),
    valueGbp: z.number(),
    /** The change from the previous point, or null when it has no value for the client. */
    changeGbp: z.number().nullable(),
});
export type BookHistoryClientRow = z.infer<typeof bookHistoryClientRowSchema>;

export const bookHistoryPointDetailSchema = z.object({
    point: bookHistoryPointSchema,
    previous: bookHistoryPointSchema.nullable(),
    drivers: bookHistoryDriversSchema.nullable(),
    /** The securities with the largest price change, largest first. */
    topMovers: z.array(bookHistoryMoverSchema),
    /**
     * The net monies and the income of the month reports of the months that
     * end after the previous point and on or before this point. Information
     * only: the drivers already hold these flows.
     */
    reportedFlows: z
        .object({
            months: z.array(z.string()),
            netMoniesGbp: z.number(),
            incomeGbp: z.number(),
        })
        .nullable(),
    /** The clients of the point. Their values add up to the value of the point. */
    clients: z.array(bookHistoryClientRowSchema),
});
export type BookHistoryPointDetail = z.infer<typeof bookHistoryPointDetailSchema>;

export const bookHistoryContract = c.router(
    {
        getHistory: {
            method: 'GET',
            path: '/desk/book-history',
            query: bookHistoryFilterQuerySchema.extend({
                range: bookHistoryRangeSchema.optional(),
            }),
            responses: { 200: bookHistoryResponseSchema },
            summary: 'The value of the book of the signed-in IM over a range, one point a period.',
        },
        getPoint: {
            method: 'GET',
            path: '/desk/book-history/point',
            query: bookHistoryFilterQuerySchema.extend({
                range: bookHistoryRangeSchema.optional(),
                /** The `date` of a point of the series of the same range. */
                date: isoDay,
            }),
            responses: { 200: bookHistoryPointDetailSchema },
            summary: 'One point of the book value history: its clients and its change drivers.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
